import { Menu } from "../views/Menu";

export const dateTFormat = (date: Date) => {
  function formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();
    const paddedMonth = month.toString().padStart(2, "0");
    const paddedDay = day.toString().padStart(2, "0");
    const paddedHours = hours.toString().padStart(2, "0");
    const paddedMinutes = minutes.toString().padStart(2, "0");
    const paddedSeconds = seconds.toString().padStart(2, "0");

    return `${year}-${paddedMonth}-${paddedDay}T${paddedHours}:${paddedMinutes}:${paddedSeconds}`;
  }
  return formatDate(date);
};

export const getEmbedUrl = (url: string | undefined) => {
  if (!url) return "";
  const videoId = url.split("v=")[1]?.split("&")[0];
  return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
};

export const getStatusLabel = (status: string | undefined) => {
  switch (status) {
    case "Pending":
      return "Pending";
    case "AcceptedByCart":
      return "Accepted By Car...";
    case "ApprovedByRestaurant":
      return "Approved By Res...";
    case "Collecting":
      return "Collecting";
    case "Preparing":
      return "Preparing";
    case "ReadyAtKitchen":
      return "Ready At Kitche...";
    case "ArrivedAtCart":
      return "Arrived At Cart";
    case "Completed":
      return "Completed";
    case "InAppCart":
      return "In App Cart";
    case "IssuedByKitchen":
      return "Issued By Kitch...";
    default:
      return "Cancelled";
  }
};

// src/utils/availabilityUtils.ts

export const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export const getCurrentDayIndex = (): number => {
  return new Date().getDay(); // Returns 0-6 (Sun-Sat)
};

export const isItemAvailableToday = (availableDays?: number[]): boolean => {
  if (!availableDays || availableDays.length === 0) return true;
  const today = getCurrentDayIndex();
  return availableDays.includes(today);
};

export const getAvailableDayNames = (availableDays?: number[]): string => {
  if (!availableDays || availableDays.length === 0) return "Every day";
  return availableDays.map(day => dayNames[day]).join(', ');
};

export const getCurrentDayName = (): string => {
  return dayNames[getCurrentDayIndex()];
};

// Add this utility function outside your component
export const sortMenusByAvailability = (menus: Menu[]) => {
  return [...menus]
    .filter(menu => menu.isAvailable)
    .sort((a, b) => {
      const isAvailableNow = (menu: Menu) => {
        const [startTimeStr, endTimeStr] = menu.servingTime?.split('-') || [];
        if (!startTimeStr || !endTimeStr) return true; // Assume available if no time specified

        const now = new Date();
        const startTime = new Date();
        const endTime = new Date();

        const [startHours, startMinutes] = startTimeStr.split(':').map(Number);
        const [endHours, endMinutes] = endTimeStr.split(':').map(Number);

        startTime.setHours(startHours, startMinutes, 0, 0);
        endTime.setHours(endHours, endMinutes, 0, 0);

        return now >= startTime && now <= endTime;
      };

      const aAvailable = isAvailableNow(a);
      const bAvailable = isAvailableNow(b);

      if (aAvailable && !bAvailable) return -1;
      if (!aAvailable && bAvailable) return 1;
      return 0;
    });
};

// utils/timeSlotsUtils.ts
// utils/scheduleUtils.ts
// export const generateAvailableSchedule = (
//   servingTime: string | undefined,
//   availableDays: number[] | undefined
// ) => {
//   if (!servingTime || !availableDays) return { dates: [], slots: [] };

//   const [startTimeStr, endTimeStr] = servingTime.split('-');
//   const [startHour, startMinute] = startTimeStr.split(':').map(Number);
//   const [endHour, endMinute] = endTimeStr.split(':').map(Number);

//   // Generate available dates (today + next 6 days)
//   const dates: { day: string; date: string; weekday: string }[] = [];
//   const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

//   for (let i = 0; i < 7; i++) {
//     const date = new Date();
//     date.setDate(date.getDate() + i);
//     const dayIndex = date.getDay();

//     if (availableDays.includes(dayIndex)) {
//       dates.push({
//         day: weekdays[dayIndex],
//         date: date.toISOString().split('T')[0],
//         weekday: i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : weekdays[dayIndex]
//       });
//     }
//   }

//   // Generate time slots (30-minute intervals)
//   const slots: string[] = [];
//   let currentHour = startHour;
//   let currentMinute = startMinute;

//   while (currentHour < endHour || (currentHour === endHour && currentMinute < endMinute)) {
//     const period = currentHour >= 12 ? 'PM' : 'AM';
//     const displayHour = currentHour > 12 ? currentHour - 12 : currentHour;

//     slots.push(
//       `${displayHour}:${currentMinute.toString().padStart(2, '0')} ${period} - ` +
//       `${(currentMinute + 30) >= 60 ? 
//         (currentHour + 1 > 12 ? currentHour + 1 - 12 : currentHour + 1) : 
//         displayHour}:${(currentMinute + 30) % 60 === 0 ? '00' : '30'} ${period}`
//     );

//     // Increment by 30 minutes
//     currentMinute += 30;
//     if (currentMinute >= 60) {
//       currentHour++;
//       currentMinute -= 60;
//     }
//   }

//   return { dates, slots };
// };

export const generateAvailableSchedule = (
  servingTime: string | undefined,
  availableDays: number[] | undefined,
  orderClosingTime: string = "10:00 PM",
  maxDaysToScheduleAhead: number = 7
) => {
  if (!servingTime || !availableDays) return { dates: [], slots: [] };

  const [startTimeStr, endTimeStr] = servingTime.split('-');
  const [startHour, startMinute] = startTimeStr.split(':').map(Number);
  const [endHour, endMinute] = endTimeStr.split(':').map(Number);

  // Parse order closing time to Date object
  const now = new Date();
  const closingTimeDate = new Date();
  const [closingHour, closingMinPart] = orderClosingTime.split(':');
  const [closingMinute, closingPeriod] = closingMinPart.split(' ');

  let closingH = parseInt(closingHour);
  if (closingPeriod === 'PM' && closingH !== 12) closingH += 12;
  if (closingPeriod === 'AM' && closingH === 12) closingH = 0;

  closingTimeDate.setHours(closingH, parseInt(closingMinute), 0, 0);

  const dates: { day: string; date: string; weekday: string }[] = [];
  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  for (let i = 0; i < maxDaysToScheduleAhead; i++) {
    const date = new Date();
    date.setDate(date.getDate() + i);
    const dayIndex = date.getDay();

    const isToday = i === 0;
    const isClosingTimePassed = isToday && now >= closingTimeDate;

    if (availableDays.includes(dayIndex) && (!isToday || !isClosingTimePassed)) {
      dates.push({
        day: weekdays[dayIndex],
        date: date.toISOString().split('T')[0],
        weekday: isToday ? 'Today' : i === 1 ? 'Tomorrow' : weekdays[dayIndex]
      });
    }
  }

  // Generate 30-min time slots
  const slots: string[] = [];
  let currentHour = startHour;
  let currentMinute = startMinute;

  while (currentHour < endHour || (currentHour === endHour && currentMinute < endMinute)) {
    const endSlotHour = currentMinute + 30 >= 60 ? currentHour + 1 : currentHour;
    const endSlotMinute = (currentMinute + 30) % 60;

    const formatTime = (h: number, m: number) => {
      const suffix = h >= 12 ? 'PM' : 'AM';
      const displayHour = h % 12 === 0 ? 12 : h % 12;
      return `${displayHour}:${m.toString().padStart(2, '0')} ${suffix}`;
    };

    slots.push(`${formatTime(currentHour, currentMinute)} - ${formatTime(endSlotHour, endSlotMinute)}`);

    currentMinute += 30;
    if (currentMinute >= 60) {
      currentHour++;
      currentMinute -= 60;
    }
  }

  return { dates, slots };
};

export const convertToISODateTime = (dateStr: string, timeStr: string): string => {
  // Convert time to 24-hour format
  const [time, modifier] = timeStr.split(' ');
  let [hours, minutes] = time.split(':');

  if (modifier === 'PM' && hours !== '12') {
    hours = String(parseInt(hours, 10) + 12);
  }
  if (modifier === 'AM' && hours === '12') {
    hours = '00';
  }

  // Create ISO string (UTC)
  const isoString = `${dateStr}T${hours.padStart(2, '0')}:${minutes}:00.000Z`;

  // Validate the format
  if (isNaN(Date.parse(isoString))) {
    console.error('Invalid date format generated:', isoString);
    return new Date().toISOString(); // Fallback to current time
  }

  return isoString;
};

export const getUserInitials = (fullName: string | undefined) => {
  if (!fullName) return 'US'; // Default if no name
  
  const names = fullName.split(' ');
  let initials = '';
  
  if (names.length > 0) {
    initials += names[0].charAt(0).toUpperCase();
  }
  
  if (names.length > 1) {
    initials += names[names.length - 1].charAt(0).toUpperCase();
  }
  
  return initials || 'US'; // Fallback if something went wrong
};

export const validateProfilePhotoUrl = (photoUrl: string | undefined | null): string | null => {
  if (!photoUrl) return null;
  
  const isDefaultGoogleImage = /(=s96-c|googleusercontent\.com\/a\/default-user)/.test(photoUrl);
  
  return isDefaultGoogleImage ? null : photoUrl;
};